import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import LoadingSpinner from "../components/LoadingSpinner";
import Section from "../components/outages/Section";
import {
  type ResponsiveColumn,
} from "../components/ResponsiveDataTable";
import SignalDot from "../components/SignalDot";
import StackedButton from "../components/StackedButton";
import StateBadge from "../components/StateBadge";
import TicketCell from "../components/TicketCell";
import { useBoxes } from "../hooks/useBoxes";
import {
  fmtCustomerWc,
  fmtDuration,
  fmtUtc,
  fmtWorkCenter,
  parseSmartboxNumber,
} from "../lib/format";
import { deriveSignalDots } from "../lib/signalDots";
import type { Box } from "../types/boxes";

/**
 * Shared column layout for all 4 outage subsections. Same as BoxesPage's
 * BOXES_COLUMNS but with `Started` instead of `Since` and a "none" pill
 * for missing tickets.
 */
const makeOutageColumns = (
  onStackedChange: (scu: string, isStacked: boolean) => void,
): ResponsiveColumn<Box>[] => [
  {
    key: "identity",
    header: "Workcenter",
    cell: (b) => {
      const sb = parseSmartboxNumber(b.starlink_name);
      return (
        <div className="text-xs">
          <div className="font-semibold">
            {fmtWorkCenter(b.work_center) || "-"}
            {sb && (
              <span className="ml-1.5 text-secondary-600 font-mono">
                · {sb}
              </span>
            )}
          </div>
          {b.customer && (
            <div className="text-secondary-500">{b.customer}</div>
          )}
          <code className="font-mono text-sm text-secondary-900 break-all">
            {b.scu_name}
          </code>
        </div>
      );
    },
  },
  {
    key: "state",
    header: "State",
    hideOnMobile: true,
    cell: (b) => <StateBadge state={b.state} />,
  },
  {
    key: "cause",
    header: "Cause",
    cell: (b) => (
      <div className="text-xs">
        <div className="font-semibold">
          {b.classification ??
            (b.open_ticket_issue_type?.startsWith("manual_")
              ? "Maintenance request"
              : "-")}
        </div>
        {b.primary_signal ? (
          <div className="text-secondary-500">via {b.primary_signal}</div>
        ) : (
          // No auto-classification (e.g. a manual request on a healthy box):
          // surface the reported issue so the row is self-explanatory.
          b.open_ticket_summary && (
            <div className="text-secondary-500">{b.open_ticket_summary}</div>
          )
        )}
        {b.publish_chain_broken && (
          <div
            className="mt-0.5 text-warning-700"
            title="Tier 3 (per-box local DB) is fresh but Tier 2 (broker Mongo) is silent. The box is scanning/imaging locally, but its events aren't reaching the central broker. Triage = fix BrokerAddress in the box's app_config, not a site visit. Auto-tickets here route to mqtt_broker_auto."
          >
            ⚠ likely MQTT (config)
          </div>
        )}
      </div>
    ),
  },
  {
    key: "pep_plug",
    header: "Pep / Plug",
    cell: (b) => {
      const dots = deriveSignalDots(b);
      return (
        <div className="flex flex-col gap-0.5">
          <SignalDot label="Pep" {...dots.pep} />
          <SignalDot label="Plug" {...dots.plug} />
          <SignalDot label="UPS" {...dots.ups} />
          <SignalDot label="TS" {...dots.ts} />
        </div>
      );
    },
  },
  {
    key: "open_for",
    header: "Open for",
    className: "text-xs",
    cell: (b) => (
      <div title={b.since_utc ? `started ${fmtUtc(b.since_utc)} UTC` : undefined}>
        <div className="font-semibold">{fmtDuration(b.elapsed_seconds)}</div>
        {b.since_utc && (
          <div className="text-secondary-500 text-[10px] whitespace-nowrap">
            since {fmtUtc(b.since_utc)}
          </div>
        )}
      </div>
    ),
  },
  {
    key: "ticket",
    header: "Ticket",
    className: "align-top min-w-[200px]",
    cell: (b) => (
      <TicketCell
        origin={b.open_ticket_origin}
        ticketId={b.open_ticket_id}
        initialAssignee={b.open_ticket_assignee}
        initialCommentCount={b.open_ticket_comment_count}
        mttrResolvedUtc={b.open_ticket_mttr_resolved_utc}
        mttrResolvedBy={b.open_ticket_mttr_resolved_by}
        box={b}
      />
    ),
  },
  {
    key: "stacked",
    header: "Stacked",
    cell: (b) => (
      <StackedButton
        scuName={b.scu_name}
        isStacked={b.is_stacked}
        setBy={b.stacked_set_by}
        onChanged={(s) => onStackedChange(b.scu_name, s)}
      />
    ),
  },
  {
    key: "actions",
    header: "",
    mobileLabel: "Detail",
    cell: (b) => (
      <Link
        to={`/signal/${encodeURIComponent(b.scu_name)}`}
        className="text-primary-600 text-xs hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        signal →
      </Link>
    ),
  },
];

/**
 * Open-outages view — derived from the boxes snapshot.
 *
 * Section order (Jason 2026-05-05): triage urgency, descending. DOWN
 * needs physical/network response; partial outages are scanner-side or
 * camera-side faults that auto-ticket but the box is still partially
 * functional; POWERED_DOWN is at the bottom because boxes powered down
 * for routine reasons (overnight, transports) shouldn't crowd out the
 * stuff that needs ops attention.
 *
 *   1. Full Outages (DOWN — incl. MQTT/CYBERPOWER classifications)
 *   2. Camera-Only (SCANNER_ONLY) — camera silent, scanner flowing
 *   3. Scanner-Only (CAMERA_ONLY) — scanner broken, camera flowing
 *   4. Powered Down — separated out so it's visible but not in the way
 *
 * MQTT-broker and CYBERPOWER outages are DOWNs with a distinct
 * `classification`; they appear in Full Outages with the cause column
 * disambiguating ticket routing (mqtt_broker_auto / cyberpower_auto).
 *
 * The snapshot only carries CURRENTLY-OPEN outage info. For closed-outage
 * history, run `python -m mttr outages --since 7d` from the CLI.
 */
export const OutagesPage: React.FC = () => {
  const { snapshot, loading, error } = useBoxes();
  const [customerFilter, setCustomerFilter] = useState<string>("");

  // Optimistic stacked overrides — when an operator clicks Mark Stacked /
  // Unstack, the POST succeeds well before the next boxes.json snapshot
  // reflects it. We record the new state here and apply it over the snapshot
  // so the box flips its button AND moves to/from the Stacked section
  // immediately, and stays put across the 30s snapshot polls (until the
  // snapshot itself catches up, at which point the values simply agree).
  const [stackedOverrides, setStackedOverrides] = useState<
    Record<string, boolean>
  >({});
  const onStackedChange = useCallback((scu: string, isStacked: boolean) => {
    setStackedOverrides((prev) => ({ ...prev, [scu]: isStacked }));
  }, []);

  const columns = useMemo(
    () => makeOutageColumns(onStackedChange),
    [onStackedChange],
  );

  // Snapshot boxes with optimistic stacked overrides applied.
  const boxes = useMemo(() => {
    const raw = snapshot?.boxes ?? [];
    if (Object.keys(stackedOverrides).length === 0) return raw;
    return raw.map((b) =>
      b.scu_name in stackedOverrides
        ? { ...b, is_stacked: stackedOverrides[b.scu_name] }
        : b,
    );
  }, [snapshot, stackedOverrides]);

  const customers = useMemo(() => {
    const set = new Set<string>();
    boxes.forEach((b) => {
      if (b.customer) set.add(b.customer);
    });
    return ["", ...Array.from(set).sort()];
  }, [boxes]);

  const filterAndSort = (predicate: (b: Box) => boolean): Box[] => {
    const cust = customerFilter.toLowerCase();
    return boxes
      .filter(predicate)
      .filter((b) => !cust || (b.customer ?? "").toLowerCase() === cust)
      .sort((a, b) => (b.elapsed_seconds ?? 0) - (a.elapsed_seconds ?? 0));
  };

  // Active sections exclude stacked boxes — those don't represent
  // actionable outages (they're intentionally offline). Stacked rows
  // get their own section at the bottom.
  const fullOutages: Box[] = useMemo(
    () => filterAndSort((b) => b.state === "DOWN" && !b.is_stacked),
    [boxes, customerFilter],
  );

  const scannerOnly: Box[] = useMemo(
    () => filterAndSort((b) => b.state === "SCANNER_ONLY" && !b.is_stacked),
    [boxes, customerFilter],
  );

  const cameraOnly: Box[] = useMemo(
    () => filterAndSort((b) => b.state === "CAMERA_ONLY" && !b.is_stacked),
    [boxes, customerFilter],
  );

  const poweredDown: Box[] = useMemo(
    () => filterAndSort((b) => b.state === "POWERED_DOWN" && !b.is_stacked),
    [boxes, customerFilter],
  );

  // Maintenance requests — boxes with an OPEN ticket that aren't in any
  // active-outage section. In practice these are healthy (UP/UNKNOWN) boxes
  // carrying a manually-reported issue (lights, starlink, electrical, camera)
  // filed via "Report Maintenance Need". The auto-detection sections above
  // never surface them because the box's state isn't an outage; without this
  // section those tickets would be invisible to operators.
  const maintenanceRequests: Box[] = useMemo(
    () =>
      filterAndSort(
        (b) =>
          b.open_ticket_id !== null &&
          b.state !== "DOWN" &&
          b.state !== "SCANNER_ONLY" &&
          b.state !== "CAMERA_ONLY" &&
          b.state !== "POWERED_DOWN" &&
          !b.is_stacked,
      ),
    [boxes, customerFilter],
  );

  // Stacked boxes — show only those that WOULD have been in an active
  // section if they weren't stacked. UP-and-stacked doesn't need
  // surfacing here.
  const stacked: Box[] = useMemo(
    () =>
      filterAndSort(
        (b) =>
          b.is_stacked &&
          b.state !== "UP" &&
          b.state !== "UNKNOWN",
      ),
    [boxes, customerFilter],
  );

  const totalOpen =
    fullOutages.length + scannerOnly.length + cameraOnly.length + poweredDown.length;

  return (
    <div>
      {/* Sticky header — title row + count chips + customer filter all
          stay pinned at the top while the sections below scroll. Mirrors
          the MapPage pattern (`sticky top-0 z-20` + white bg + border).
          Body content gets its own padding below; root has none so the
          sticky band reaches edge-to-edge. */}
      <header className="sticky top-0 z-20 px-4 md:px-6 py-3 border-b border-secondary-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold">Maintenance</h1>
            <div className="text-xs text-secondary-500 mt-1 flex flex-wrap items-center gap-2 min-w-0">
              <span className="shrink-0">
                {totalOpen} open outage{totalOpen === 1 ? "" : "s"}
                {maintenanceRequests.length > 0 &&
                  ` · ${maintenanceRequests.length} request${maintenanceRequests.length === 1 ? "" : "s"}`}
              </span>
              {fullOutages.length > 0 && (
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-error-50 text-error-800 border border-error-300">
                  {fullOutages.length} full
                </span>
              )}
              {scannerOnly.length > 0 && (
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-warning-50 text-warning-700 border border-warning-400">
                  {scannerOnly.length} camera-faulted
                </span>
              )}
              {cameraOnly.length > 0 && (
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-300">
                  {cameraOnly.length} scanner-faulted
                </span>
              )}
              {poweredDown.length > 0 && (
                <span className="shrink-0 px-2 py-0.5 rounded-full bg-error-100 text-error-900 border border-error-500">
                  {poweredDown.length} powered-down
                </span>
              )}
              {maintenanceRequests.length > 0 && (
                <span
                  className="shrink-0 px-2 py-0.5 rounded-full bg-primary-50 text-primary-800 border border-primary-300"
                  title="Manually-reported maintenance needs on otherwise-healthy boxes (lights, starlink, etc.)"
                >
                  {maintenanceRequests.length} maintenance
                </span>
              )}
              {stacked.length > 0 && (
                <span
                  className="shrink-0 px-2 py-0.5 rounded-full bg-secondary-200 text-secondary-800 border border-secondary-400"
                  title="Boxes intentionally offline (stacked / in storage)"
                >
                  {stacked.length} stacked
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="px-3 py-2 min-h-[44px] border border-secondary-300 rounded-md text-sm bg-white"
            >
              {customers.map((c) => (
                <option key={c || "(any)"} value={c}>
                  {c || "All customers"}
                </option>
              ))}
            </select>
            <div className="hidden lg:block text-xs text-secondary-500">
              For closed-outage history, run{" "}
              <code className="px-1.5 py-0.5 bg-secondary-100 rounded">
                python -m mttr outages --since 7d
              </code>
            </div>
          </div>
        </div>
      </header>

      <div className="p-4 md:p-6">

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error.message}
        </div>
      )}

      {loading && !snapshot ? (
        <LoadingSpinner label="loading maintenance" />
      ) : totalOpen + maintenanceRequests.length === 0 ? (
        <div className="text-sm text-secondary-500 bg-white p-6 rounded-md border border-secondary-200">
          No open outages or maintenance requests. ✓
        </div>
      ) : (
        <>
          <Section
            title="Full Outages"
            subtitle="DOWN — physical or network response needed. Cause column disambiguates HARDWARE / SOFTWARE / MQTT (broker pipeline broken) / CYBERPOWER (UPS draining)."
            accentBorder="border-error-500"
            accentText="text-error-800"
            rows={fullOutages}
            emptyMessage="No full outages currently. ✓"
            columns={columns}
          />

          <div className="mt-6">
            <Section
              title="Camera faulted"
              subtitle="RFID is flowing but camera is silent across all 3 sources. Auto-ticketed as camera_only_auto. (State name internally: SCANNER_ONLY — the scanner is the one signal that's up.)"
              accentBorder="border-warning-400"
              accentText="text-warning-700"
              rows={scannerOnly}
              emptyMessage="No camera-faulted outages — every box scanning tags is also publishing camera signal."
              columns={columns}
            />
          </div>

          <div className="mt-6">
            <Section
              title="Scanner faulted"
              subtitle="Camera is publishing but RFID has been silent past the silence threshold (typical off-shift). Auto-ticketed as scanner_only_auto. (State name internally: CAMERA_ONLY — the camera is the one signal that's up.)"
              accentBorder="border-yellow-300"
              accentText="text-yellow-700"
              rows={cameraOnly}
              emptyMessage="No scanner-faulted outages — every box with camera signal is also scanning tags."
              columns={columns}
            />
          </div>

          <div className="mt-6">
            <Section
              title="Maintenance Requests"
              subtitle="Manually-reported issues (lights, starlink, electrical, camera) on boxes that are otherwise healthy — filed via 'Report Maintenance Need' on the map / box cards. Not auto-detected outages; they need a maintenance response, not triage."
              accentBorder="border-primary-400"
              accentText="text-primary-800"
              rows={maintenanceRequests}
              emptyMessage="No open maintenance requests."
              columns={columns}
            />
          </div>

          <div className="mt-6">
            <Section
              title="Powered Down"
              subtitle="All 3 power signals (peplink / smart plug / tailscale) confirmed offline. Often routine — overnight shutdowns, transports — but kept visible at the bottom for fleet awareness."
              accentBorder="border-error-500"
              accentText="text-error-900"
              rows={poweredDown}
              emptyMessage="No boxes currently powered down."
              columns={columns}
            />
          </div>

          {stacked.length > 0 && (
            <div className="mt-6">
              <Section
                title="Stacked"
                subtitle="Intentionally offline (workcenter marked Stacked OR an MTTR operator stacked the box). No new tickets fire and the box stays out of active counts. Click Unstack to return it to normal triage."
                accentBorder="border-secondary-400"
                accentText="text-secondary-800"
                rows={stacked}
                emptyMessage="No stacked boxes."
                columns={columns}
              />
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};

export default OutagesPage;
